import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Home,
  Mail,
  BarChart3,
  Users as UsersIcon,
  Settings as SettingsIcon,
  ChevronLeft,
  FileBarChart,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { UserMenu } from './UserMenu';
import { features } from '../../app/feature-registry';
import { can } from '../../app/policy';
import { Badge } from '../../ui';
import { usePendingProposalsCount } from '../../features/aprovacoes/hooks/useAgentProposals';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  badge?: number;
}

interface NavSection {
  key: string;
  label?: string;
  items: NavItem[];
  visible: boolean;
}

// Workspace items not yet migrated to feature modules. As features migrate,
// move them out of here and into feature-registry.ts.
const STATIC_WORKSPACE_ITEMS: NavItem[] = [
  { to: '/hoje', icon: Home, label: 'Hoje', end: true },
  // Pipeline comes from registry (anchor: 'pipeline')
  { to: '/campanhas', icon: Mail, label: 'Campanhas' },
  { to: '/insights', icon: BarChart3, label: 'Insights' },
  { to: '/kpis', icon: FileBarChart, label: 'KPIs' },
  { to: '/clientes', icon: UsersIcon, label: 'Clientes' },
];

// Position registry items by id; unknown ids append at the end.
const WORKSPACE_ANCHOR_ORDER: string[] = [
  'hoje',
  'pipeline',
  'campanhas',
  'insights',
  'kpis',
  'financeiro',
  'clientes',
];

export function Sidebar() {
  const { user } = useAuth();
  const { isCollapsed, toggle } = useSidebar();

  const isAdmin = user?.role === 'ADMIN';
  const isManagerOrAbove = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  // Só o ADMIN decide propostas; para os outros o hook nem consulta a API.
  const pendingProposals = usePendingProposalsCount(isAdmin);

  // Compose workspace items: static entries + registry features the user can see,
  // ordered by anchor positions. Unknown registry ids get appended.
  const workspaceItems: NavItem[] = (() => {
    const fromRegistry = features
      .filter((f) => f.sidebarSection === 'workspace')
      .filter((f) => !f.requiredPermission || can(user?.role ?? null, f.requiredPermission))
      .map<NavItem>((f) => ({
        to: f.basePath,
        icon: f.icon,
        label: f.label,
        ...(f.id === 'aprovacoes' && pendingProposals > 0 ? { badge: pendingProposals } : {}),
      }));

    const registryById = new Map(fromRegistry.map((it) => [it.to.replace(/^\//, ''), it]));
    const staticByPath = new Map(
      STATIC_WORKSPACE_ITEMS.map((it) => [it.to.replace(/^\//, ''), it])
    );

    const ordered: NavItem[] = [];
    for (const id of WORKSPACE_ANCHOR_ORDER) {
      const item = registryById.get(id) ?? staticByPath.get(id);
      if (item) {
        ordered.push(item);
      }
    }

    // Append any registry features whose id wasn't anchored
    for (const item of fromRegistry) {
      const id = item.to.replace(/^\//, '');
      if (!WORKSPACE_ANCHOR_ORDER.includes(id)) {
        ordered.push(item);
      }
    }
    return ordered;
  })();

  const sections: NavSection[] = [
    {
      key: 'workspace',
      items: workspaceItems,
      visible: true,
    },
    {
      key: 'config',
      label: 'Configuração',
      items: [{ to: '/config', icon: SettingsIcon, label: 'Configuração' }],
      visible: isAdmin || isManagerOrAbove,
    },
  ];

  return (
    <aside
      className={clsx(
        'h-screen border-r border-border bg-surface flex flex-col transition-[width] duration-200',
        isCollapsed ? 'w-16' : 'w-60'
      )}
    >
      <div className="h-14 flex items-center justify-between px-3 border-b border-border">
        {!isCollapsed && <span className="font-serif text-lg font-semibold text-ink">Atlas</span>}
        <button
          type="button"
          onClick={toggle}
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          className="ml-auto p-1.5 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-subtle"
        >
          <ChevronLeft
            className={clsx('w-4 h-4 transition-transform', isCollapsed && 'rotate-180')}
          />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {sections
          .filter((s) => s.visible)
          .map((section, idx) => (
            <div key={section.key} className={idx > 0 ? 'mt-4 pt-4 border-t border-border' : ''}>
              {!isCollapsed && section.label && (
                <p className="px-3 mb-1 text-[10px] uppercase tracking-wider text-ink-tertiary font-semibold">
                  {section.label}
                </p>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-sm transition-colors',
                      isActive
                        ? 'bg-subtle text-ink-primary font-medium'
                        : 'text-ink-secondary hover:bg-subtle hover:text-ink-primary'
                    )
                  }
                >
                  <item.icon size={18} className="shrink-0" />
                  {!isCollapsed && <span>{item.label}</span>}
                  {!isCollapsed && item.badge !== undefined && (
                    <Badge
                      tone="warning"
                      className="ml-auto"
                      aria-label={`${item.badge} pendentes`}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
      </nav>

      <div className="border-t border-border p-3">
        <UserMenu />
      </div>
    </aside>
  );
}
